import React, { useState, useEffect, useRef, useCallback } from "react";
import CheckboxList from "./CheckboxList";
import { Products } from "./Card";
import "./Search.css";
import searchProducts from "../../services/searchService";
import { groupProducts } from "../../services/groupProducts";
import { useFilterOptions } from "../../services/filtersContext";
import { useAuth } from "../../services/authContext";
import Loader from "../Loader";
import AuthModal from "../AuthModal";
import { useLocation } from "react-router-dom";

function Search() {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const genderParam = params.get("gender");

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const observer = useRef(null);

  const { brands, sizes, genders, colors, categories } = useFilterOptions();

  const handleCheckboxChange = (checkedValues, setType) => {
    setType(checkedValues);
  };

  const fetchProducts = useCallback(
    async (reset = false, overrideGender = null) => {
      if (isLoading || !hasMore) return;
      setIsLoading(true);

      const currentPage = reset ? 1 : page;

      const data = {
        brands: selectedBrands,
        sizes: selectedSizes,
        genders: overrideGender ? [overrideGender] : selectedGenders,
        colors: selectedColors,
        categories: selectedCategories,
      };

      try {
        const newProducts = await searchProducts(data, currentPage);

        setProducts((prevProducts) => (reset ? newProducts : [...prevProducts, ...newProducts]));

        if (newProducts.length < 100) {
          setHasMore(false);
        } else {
          setPage((prevPage) => prevPage + 1);
        }
      } catch (error) {
        console.error("Failed to search products:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, hasMore, page, selectedBrands, selectedSizes, selectedGenders, selectedColors, selectedCategories]
  );

  const handleSubmit = () => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    fetchProducts(true);
  };

  // ✅ Auto-trigger gender from query param and override during fetch
  useEffect(() => {
    if (!hasAutoTriggered && genderParam) {
      setSelectedGenders([genderParam]);
      setHasAutoTriggered(true);
      fetchProducts(true, genderParam); // ← pass directly
    }
  }, [genderParam, hasAutoTriggered, fetchProducts]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore) return;

    const observerInstance = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          fetchProducts();
        }
      },
      { threshold: 1.0 }
    );

    if (observer.current) observerInstance.observe(observer.current);

    return () => {
      if (observer.current) observerInstance.unobserve(observer.current);
    };
  }, [fetchProducts, hasMore, isLoading]);

  if (!user) {
    return (
      <div className="search-gate">
        <div className="search-gate-card">
          <div className="search-gate-icon">🔍</div>
          <h1 className="search-gate-title">Browse the full catalog</h1>
          <p className="search-gate-subtitle">
            Log in to search, filter by brand, size, and color, and save what you love.
          </p>
          <button className="search-gate-btn" onClick={() => setShowAuth(true)}>
            Log In / Sign Up
          </button>
        </div>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </div>
    );
  }

  return (
    <div className="browse-page">
      <div className="browse-header">
        <h1 className="browse-title">Browse All Deals</h1>
        <p className="browse-subtitle">Filter by brand, size, color, and more to find exactly what you want</p>
      </div>
    <div className="search-container">
      <div className="filter-sidebar">
        <h2>FILTERS</h2>
        <CheckboxList
          values={brands}
          title="Brand"
          onChange={(vals) => handleCheckboxChange(vals, setSelectedBrands)}
          checkedValues={selectedBrands}
        />
        <CheckboxList
          values={sizes}
          title="Size"
          onChange={(vals) => handleCheckboxChange(vals, setSelectedSizes)}
          checkedValues={selectedSizes}
        />
        <CheckboxList
          values={genders}
          title="Gender"
          onChange={(vals) => handleCheckboxChange(vals, setSelectedGenders)}
          checkedValues={selectedGenders}
        />
        <CheckboxList
          values={colors}
          title="Color"
          onChange={(vals) => handleCheckboxChange(vals, setSelectedColors)}
          checkedValues={selectedColors}
        />
        <CheckboxList
          values={categories}
          title="Category"
          onChange={(vals) => handleCheckboxChange(vals, setSelectedCategories)}
          checkedValues={selectedCategories}
        />
        <button className="apply-button" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <Loader /> : "Apply"}
        </button>
      </div>

      <div className="product-display">
        <Products products={groupProducts(products)} />
        <div ref={observer} className="load-more">
          {isLoading && <Loader />}
        </div>
      </div>
    </div>
    </div>
  );
}

export default Search;
